import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaSuitcase,
  FaGasPump,
  FaRupeeSign,
  FaRoute,
  FaTimes,
  FaInfoCircle,
  FaSnowflake,
  FaMoneyBillWave,
  FaCreditCard,
  FaSortAmountDown,
  FaTag,
  FaHourglassHalf,
  FaEdit,
} from "react-icons/fa";
import api from "../api/Api";

import Loader from "../components/trip_loading";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;
const CAR_SKELETON =
  "https://stylemixthemes.com/car-dealer-plugin/_ipx/f_png&q_75&s_1008x624/car-tesla-1.png";

export default function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();

  const [sortType, setSortType] = useState("price_asc");

  const [searchDetails, setSearchDetails] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCar, setSelectedCar] = useState(null);

  useEffect(() => {
    if (!location.state) {
      navigate("/dashboard");
      return;
    }

    const details = location.state;
    setSearchDetails(details);

    const fetchCars = async () => {
      try {
        setLoading(true);
        const {
          pickCityId,
          durationId,
          durationHours,
          taxiId,
          activeTab,
          timeName,
          date,
          serviceTypeId,
        } = details;

        if (activeTab === "local" && (!pickCityId || !durationId)) {
          setError("Pickup City or Duration not selected.");
          return;
        } else if (activeTab === "taxi" && (!pickCityId || !taxiId)) {
          setError("Pickup City or Taxi Package not selected.");
          return;
        } else if (
          (activeTab === "oneway" || activeTab === "round") &&
          !pickCityId
        ) {
          setError("Pickup location not selected.");
          return;
        }

        // Night time logic using the local variable
        let isNightTime = false;
        if (timeName) {
          const timeStr = timeName.toLowerCase();
          const hourMatch = timeStr.match(/\d+/);
          if (hourMatch) {
            let hour = parseInt(hourMatch[0], 10);
            if (timeStr.includes("pm") && hour !== 12) hour += 12;
            if (timeStr.includes("am") && hour === 12) hour = 0;

            if (hour >= 23 || hour <= 6) {
              isNightTime = true;
            }
          }
        }
        const distance = details.distance || 0;
        const durationDays = details.totalDays || 1;

        // Payload uses the local variables
        const payload = {
          pickCityId: pickCityId,
          serviceTypeId: serviceTypeId,
          activeTab: activeTab,
          dateIn: date,
          timeId: details.timeId,
          isNightTime: isNightTime,
          durationId: activeTab === "local" ? durationId : undefined,
          durationHours: activeTab === "local" ? durationHours : undefined,
          taxiId: activeTab === "taxi" ? taxiId : undefined,

          distance: distance,
          durationDays: durationDays,
        };

        let apiEndpoint = "";
        if (activeTab === "local") apiEndpoint = "/carRoutes/local";
        else if (activeTab === "oneway") apiEndpoint = "/carRoutes/oneway";
        else if (activeTab === "round") apiEndpoint = "/carRoutes/roundtrip";
        else if (activeTab === "taxi") apiEndpoint = "/carRoutes/taxi";

        const response = await api.post(apiEndpoint, payload);
        //  console.log("API Response:", response.data);

        const carsData = response.data || [];
        const seen = new Set();
        const uniqueCars = carsData.filter((car) => {
          if (seen.has(car.modelId)) return false;
          seen.add(car.modelId);
          return true;
        });

        // CHANGE setCars mapping
        setCars(
          uniqueCars.map((car) => {
            let validImage = car.image;
            if (validImage && validImage.includes("undefined")) {
              const filename = validImage.split("/").pop();
              validImage = `https://www.patratravels.com/admin/image/useridntimg/${filename}`;
            }

            return {
              id: car.modelId,
              carId: car.modelId,
              carName: car.name,
              name: car.name,
              image: validImage || CAR_SKELETON,
              seats: car.seats,
              ac: car.ac,
              luggage: car.bags || 2,
              fuel: "Diesel",

              // Map ALL exact pricing variables from backend
              rawBasePrice: car.rawBasePrice,
              basePrice: car.basePrice,
              finalPrice: car.finalPrice,
              crossedOutPrice: car.crossedOutPrice,
              discountAmount: car.discountAmount,
              gst: car.gst,
              gstAmount: car.gstAmount,
              driverAllowance: car.driverAllowance || 0,
              nightCharge: car.nightCharge || 0,
              tariffId: car.tariffId || null,
              kmIncluded: car.kmIncluded,
              hrIncluded: car.hrIncluded,

              extraKmUsed: car.extraKmUsed || 0,
              extraKmCharge: car.extraKmCharge || 0,
              extraKmRate: car.extraKmRate || 0,
              extraHrRate: car.extraHrRate || 0,

              type: car.category,
            };
          }),
        );
      } catch (err) {
        console.error("Car Load Error:", err);
        setError("Could not load vehicles.");
      } finally {
        setLoading(false);
      }
    };

    if (details?.pickCityId) {
      fetchCars();
    } else {
      setLoading(false);
    }
  }, [location.state, navigate]);

  const sortedCars = useMemo(() => {
    if (!cars || cars.length === 0) return [];

    const sortedArray = [...cars];
    sortedArray.sort((a, b) => {
      const priceA = a.finalPrice;
      const priceB = b.finalPrice;

      switch (sortType) {
        case "price_asc":
          return priceA - priceB;
        case "price_desc":
          return priceB - priceA;
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
    return sortedArray;
  }, [cars, sortType]);

  if (!searchDetails) return null;

  const renderSummary = () => {
    const {
      activeTab,
      pickCityName,
      pickupAddress,
      dropAddress,
      date,
      timeId,
      timeName,
      returnDate,
      returnTimeId,
      returnTimeName,
      durationName,
      durationHours,
      taxipkg,
      distance,
      totalDays,
    } = searchDetails;

    const formatDate = (d) => {
      if (!d) return "";
      return new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    return (
      <div
        className="mx-2 mt-2 mb-3 animate-fade-in"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
        }}
      >
        {/* TOP BANNER - TRIP TYPE */}
        <div
          className="px-3 py-2 d-flex justify-content-between align-items-center"
          style={{
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <span
              className="fw-bold"
              style={{
                fontSize: "0.65rem",
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Trip Details
            </span>
            <span
              className="badge rounded-pill shadow-sm"
              style={{
                backgroundColor: "#fde047",
                color: "#854d0e",
                fontSize: "0.6rem",
                fontWeight: "800",
                padding: "4px 8px",
                letterSpacing: "0.5px",
              }}
            >
              {activeTab === "round"
                ? "Round Trip"
                : activeTab === "oneway"
                  ? "One Way"
                  : activeTab === "taxi"
                    ? "Taxi Package"
                    : "Local Trip"}
            </span>
          </div>

          <button
            onClick={() => navigate("/book-ride", { state: searchDetails })}
            className="btn btn-sm d-flex align-items-center gap-1 rounded-pill shadow-sm btn-native-press"
            style={{
              backgroundColor: "#ffffff",
              color: "#0ea5e9",
              border: "1px solid #e0f2fe",
              fontSize: "0.65rem",
              fontWeight: "800",
              padding: "4px 12px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <FaEdit size={10} /> Edit
          </button>
        </div>

        <div className="p-3">
          <div className="d-flex">
            {/* TIMELINE VISUAL (DOTS & LINES) */}
            <div className="d-flex flex-column align-items-center me-3 mt-1">
              <div
                className="rounded-circle"
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: "#10b981",
                  border: "3px solid #d1fae5",
                  zIndex: 2,
                }}
              ></div>
              {(activeTab === "oneway" || activeTab === "round") && (
                <>
                  <div
                    style={{
                      flexGrow: 1,
                      width: "2px",
                      backgroundColor: "#cbd5e1",
                      margin: "2px 0",
                      minHeight: "30px",
                    }}
                  ></div>
                  <div
                    className="rounded-circle"
                    style={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: "#ef4444",
                      border: "3px solid #fee2e2",
                      zIndex: 2,
                    }}
                  ></div>
                </>
              )}
            </div>

            {/* LOCATION DETAILS */}
            <div className="flex-grow-1">
              {/* PICKUP SECTION */}
              <div
                className={
                  activeTab === "oneway" || activeTab === "round"
                    ? "mb-4"
                    : "mb-1"
                }
              >
                <div
                  style={{
                    fontSize: "0.65rem",
                    color: "#64748b",
                    textTransform: "uppercase",
                    fontWeight: "800",
                    letterSpacing: "0.5px",
                    marginBottom: "2px",
                  }}
                >
                  Pickup
                </div>
                <div
                  className="fw-bold text-dark lh-sm"
                  style={{ fontSize: "1.05rem", marginBottom: "8px" }}
                >
                  {pickupAddress || pickCityName}
                </div>

                {/* NATIVE DATE/TIME CHIPS */}
                <div className="d-flex flex-wrap gap-2">
                  <div
                    className="d-flex align-items-center rounded-pill px-2 py-1"
                    style={{
                      backgroundColor: "#f1f5f9",
                      fontSize: "0.75rem",
                      color: "#334155",
                      fontWeight: "600",
                    }}
                  >
                    <FaCalendarAlt className="me-2 text-primary opacity-75" />{" "}
                    {formatDate(date)}
                  </div>
                  <div
                    className="d-flex align-items-center rounded-pill px-2 py-1"
                    style={{
                      backgroundColor: "#f1f5f9",
                      fontSize: "0.75rem",
                      color: "#334155",
                      fontWeight: "600",
                    }}
                  >
                    <FaClock className="me-2 text-warning opacity-100" />{" "}
                    {timeName || timeId}
                  </div>
                </div>

                {/* LOCAL DURATION BADGE */}
                {activeTab === "local" && durationName && (
                  <div
                    className="mt-2 d-inline-flex align-items-center rounded-pill px-2 py-1"
                    style={{
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bae6fd",
                      fontSize: "0.75rem",
                      color: "#0284c7",
                      fontWeight: "700",
                    }}
                  >
                    <FaHourglassHalf className="me-1" size={10} />{" "}
                    {durationName}
                  </div>
                )}

                {/* TAXI PACKAGE DETAILS */}
                {activeTab === "taxi" && taxipkg && (
                  <div className="mt-3 p-2 bg-light rounded-4 d-flex align-items-center gap-3 border border-light shadow-sm">
                    <div
                      className="bg-white rounded-3 p-1 d-flex align-items-center justify-content-center"
                      style={{ width: "50px", height: "45px", flexShrink: 0 }}
                    >
                      <img
                        src={
                          searchDetails.taxiImage
                            ? `${IMG_BASE}/${searchDetails.taxiImage}`
                            : "https://cdn-icons-png.flaticon.com/512/1048/1048314.png"
                        }
                        alt={taxipkg}
                        className="img-fluid"
                        style={{ maxHeight: "100%", objectFit: "contain" }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div
                        className="fw-bold text-success lh-sm mb-1"
                        style={{ fontSize: "0.85rem" }}
                      >
                        {taxipkg}
                      </div>
                      <div
                        className="text-muted"
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        Selected Package
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* DROP-OFF SECTION */}
              {(activeTab === "oneway" || activeTab === "round") && (
                <div>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      color: "#64748b",
                      textTransform: "uppercase",
                      fontWeight: "800",
                      letterSpacing: "0.5px",
                      marginBottom: "2px",
                    }}
                  >
                    Drop-off
                  </div>
                  <div
                    className="fw-bold text-dark lh-sm"
                    style={{ fontSize: "1.05rem", marginBottom: "8px" }}
                  >
                    {dropAddress || "Destination"}
                  </div>

                  {activeTab === "round" && returnDate && (
                    <div className="d-flex flex-wrap gap-2">
                      <div
                        className="d-flex align-items-center rounded-pill px-2 py-1"
                        style={{
                          backgroundColor: "#fef2f2",
                          fontSize: "0.75rem",
                          color: "#b91c1c",
                          fontWeight: "600",
                        }}
                      >
                        <FaCalendarAlt className="me-2 opacity-75" /> Ret:{" "}
                        {formatDate(returnDate)}
                      </div>
                      <div
                        className="d-flex align-items-center rounded-pill px-2 py-1"
                        style={{
                          backgroundColor: "#f1f5f9",
                          fontSize: "0.75rem",
                          color: "#334155",
                          fontWeight: "600",
                        }}
                      >
                        <FaClock className="me-2 text-warning opacity-100" />{" "}
                        {returnTimeName || returnTimeId}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BOTTOM INFO BAR (DISTANCE & DAYS) */}
        {distance > 0 && (
          <div
            className="px-3 py-2 d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: "#f0fdf4",
              borderTop: "1px solid #dcfce7",
            }}
          >
            <div
              className="d-flex align-items-center text-success fw-bold"
              style={{ fontSize: "0.8rem" }}
            >
              <FaRoute className="me-2 opacity-75" size={14} /> Est. Distance:{" "}
              {distance} km
            </div>
            {activeTab === "round" && (
              <div
                className="d-flex align-items-center text-success fw-bold"
                style={{ fontSize: "0.8rem" }}
              >
                <div
                  className="vr me-2 opacity-25"
                  style={{ height: "14px" }}
                ></div>
                <FaCalendarAlt className="me-2 opacity-75" size={13} />{" "}
                {totalDays} Day(s)
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const CarDetailModal = ({ car, onClose }) => {
    if (!car) return null;

    const totalAmount = car.finalPrice || 0;
    const discountAmount = car.discountAmount || 0;
    const originalAmount = car.crossedOutPrice || totalAmount + discountAmount;

    const advanceAmount = Math.round(totalAmount * 0.2);
    const driverPayAmount = totalAmount - advanceAmount;

    const activeTab = searchDetails?.activeTab || "local";
    const totalDays = searchDetails?.totalDays || 1;

    const handleBooking = (paymentMode) => {
      onClose();

      navigate("/booking", {
        state: {
          car: {
            ...car,
            carId: car.id || car.modelId,
            carName: car.name,
          },
          searchDetails: {
            ...searchDetails,
            time: searchDetails.timeId,
            returnTime: searchDetails.returnTimeId,
            serviceTypeId: searchDetails.serviceTypeId,
          },
          bookingType: {
            mode: paymentMode,
            originalCost: originalAmount,
            totalAmount: totalAmount,
            payableNow: paymentMode === "partial" ? advanceAmount : totalAmount,
            payableAtDriver: paymentMode === "partial" ? driverPayAmount : 0,
            discountAmount: discountAmount,
          },
        },
      });
    };

    return (
      <div className="modal-backdrop-custom" onClick={onClose}>
        <div
          className="modal-content-custom shadow-lg d-flex flex-column"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-100 d-flex justify-content-center pt-3 pb-1">
            <div
              className="bg-secondary rounded-pill"
              style={{ width: "40px", height: "5px", opacity: 0.3 }}
            ></div>
          </div>
          <div className="d-flex justify-content-between align-items-center px-4 pb-3 border-bottom">
            <h5 className="m-0 fw-bold text-dark">Review Vehicle</h5>
            <div
              onClick={onClose}
              className="bg-light rounded-circle d-flex align-items-center justify-content-center cursor-pointer"
              style={{ width: "32px", height: "32px" }}
            >
              <FaTimes className="text-secondary" />
            </div>
          </div>
          <div className="p-0 overflow-auto flex-grow-1">
            <div className="bg-light p-4 text-center">
              <img
                src={car.image}
                alt={car.name}
                className="img-fluid drop-shadow"
                style={{ maxHeight: "160px", objectFit: "contain" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = CAR_SKELETON;
                }}
              />
            </div>
            <div className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h4 className="fw-bold m-0 text-dark">{car.name}</h4>
                  <span className="badge bg-dark text-white small mt-1 px-2">
                    {car.category || car.type}
                  </span>
                </div>
                <div className="text-end">
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    {discountAmount > 0 && (
                      <small className="text-muted text-decoration-line-through">
                        ₹{originalAmount.toLocaleString()}
                      </small>
                    )}
                    <h3 className="fw-bold text-success m-0">
                      ₹{totalAmount.toLocaleString()}
                    </h3>
                  </div>
                  <small className="text-muted fw-semibold">Total Fare</small>
                </div>
              </div>
              <hr className="my-3 text-muted opacity-25" />
              <h6 className="fw-bold mb-3 text-dark small text-uppercase">
                Vehicle Features
              </h6>
              <div className="row g-2 mb-4">
                <div className="col-6">
                  <div className="p-2 border border-light rounded-3 bg-light small fw-semibold text-secondary">
                    <FaUser className="text-muted me-2" /> {car.seats} Seats
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-2 border border-light rounded-3 bg-light small fw-semibold text-secondary">
                    <FaSuitcase className="text-muted me-2" />{" "}
                    {car.luggage || 2} Bags
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-2 border border-light rounded-3 bg-light small fw-semibold text-secondary">
                    <FaSnowflake className="text-info me-2" /> AC ({car.ac})
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-2 border border-light rounded-3 bg-light small fw-semibold text-secondary">
                    <FaGasPump className="text-danger me-2" />{" "}
                    {car.fuel || "Diesel"}
                  </div>
                </div>
              </div>

              <h6 className="fw-bold mb-2 text-dark small text-uppercase">
                Fare Breakdown
              </h6>

              <div className="bg-light p-3 rounded-4 small mb-3 border border-light">
                {/* DYNAMIC BASE FARE ROW */}
                <div className="d-flex justify-content-between mb-2 text-muted">
                  <span>
                    <span className="text-dark fw-semibold">Rental Price</span>
                    {activeTab === "local" && (
                      <small
                        className="d-block text-secondary mt-1"
                        style={{ fontSize: "0.7rem" }}
                      >
                        (Includes {car.hrIncluded} hrs & {car.kmIncluded} km)
                      </small>
                    )}
                    {(activeTab === "oneway" || activeTab === "round") && (
                      <small
                        className="d-block text-secondary mt-1"
                        style={{ fontSize: "0.7rem" }}
                      >
                        (Includes {car.kmIncluded} km)
                      </small>
                    )}
                  </span>
                  <span className="text-dark fw-semibold">
                    ₹{(car.rawBasePrice || car.basePrice || 0).toLocaleString()}
                  </span>
                </div>

                {car.extraKmCharge > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-muted">
                    <span>
                      <span className="text-dark fw-semibold">
                        Extra Km Charges
                      </span>
                      <small
                        className="d-block text-secondary mt-1"
                        style={{ fontSize: "0.7rem" }}
                      >
                        (Extra {car.extraKmUsed}km of usage)
                      </small>
                    </span>
                    <span className="text-dark fw-semibold">
                      ₹{car.extraKmCharge.toLocaleString()}
                    </span>
                  </div>
                )}

                {/* DRIVER ALLOWANCE (Round Trips) */}
                {car.driverAllowance > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-muted">
                    <span>
                      <span className="text-dark fw-semibold">
                        Driver Allowance
                      </span>
                      {activeTab === "round" && (
                        <small
                          className="d-block text-secondary mt-1"
                          style={{ fontSize: "0.7rem" }}
                        >
                          (₹{car.driverAllowance / totalDays} × {totalDays}{" "}
                          Days)
                        </small>
                      )}
                    </span>
                    <span className="text-dark fw-semibold">
                      ₹{car.driverAllowance.toLocaleString()}
                    </span>
                  </div>
                )}

                {/* GST */}
                <div className="d-flex justify-content-between mb-2 text-muted">
                  <span className="text-dark fw-semibold">
                    GST ({car.gst ?? 0}%)
                  </span>
                  <span className="text-dark fw-semibold">
                    ₹{(car.gstAmount ?? 0).toLocaleString()}
                  </span>
                </div>

                {/* NIGHT CHARGE (Local / One Way) */}
                {car.nightCharge > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-secondary">
                    <span className="text-dark fw-semibold">Night Charge</span>
                    <span className="fw-bold text-dark">
                      ₹{car.nightCharge.toLocaleString()}
                    </span>
                  </div>
                )}

                {/* DISCOUNT */}
                {discountAmount > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-success fw-bold">
                    <span>
                      <FaTag className="me-1" /> Special Discount
                    </span>
                    <span>-₹{discountAmount.toLocaleString()}</span>
                  </div>
                )}

                <div className="border-top border-dashed my-2"></div>
                <div className="d-flex justify-content-between fw-bold text-dark fs-6 mt-2">
                  <span>Total Payable</span>
                  <span className="text-success">
                    ₹{totalAmount.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* DYNAMIC EXTRA RATES BOX */}
              {car.extraKmRate > 0 && (
                <div className="p-3 bg-info bg-opacity-10 border border-info border-opacity-25 rounded-4 small mb-3">
                  <strong
                    className="d-block text-info mb-1"
                    style={{ fontSize: "0.75rem", textTransform: "uppercase" }}
                  >
                    Additional Charges (If Applicable)
                  </strong>
                  <div
                    className="text-secondary"
                    style={{ fontSize: "0.8rem" }}
                  >
                    <span className="d-block mb-1">
                      • Extra Km: <b>₹{car.extraKmRate}/km</b> (After{" "}
                      {car.kmIncluded} km)
                    </span>
                    {activeTab === "local" && car.extraHrRate > 0 && (
                      <span className="d-block">
                        • Extra Hour: <b>₹{car.extraHrRate}/hr</b> (After{" "}
                        {car.hrIncluded} hrs)
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="p-3 bg-warning bg-opacity-10 rounded-4 small d-flex align-items-start border border-warning border-opacity-25">
                <FaInfoCircle
                  className="me-2 mt-1 text-warning flex-shrink-0"
                  size={16}
                />
                <div className="text-dark">
                  <strong className="d-block mb-1">
                    Advance Payment Details
                  </strong>
                  <div className="mb-1">
                    • Pay <strong>20% now</strong> to confirm your booking. The
                    remaining{" "}
                    <strong>₹{driverPayAmount.toLocaleString()}</strong> can be
                    paid directly to the driver via <strong>Cash / UPI</strong>.
                  </div>
                  <div className="text-success fw-semibold">
                    • Switch to <strong>Full Payment</strong> and get an instant{" "}
                    <strong> 3% discount</strong> on your total fare!
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-3 border-top bg-white shadow-lg z-index-1">
            <div className="row g-2">
              <div className="col-6">
                <button
                  className="btn w-100 py-2 rounded-4 d-flex flex-column justify-content-center align-items-center border border-warning shadow-sm"
                  style={{ backgroundColor: "#fff8e1", color: "#856404" }}
                  onClick={() => handleBooking("partial")}
                >
                  <div className="fw-bold d-flex align-items-center gap-1">
                    <FaMoneyBillWave /> Pay ₹{advanceAmount.toLocaleString()}
                  </div>
                  <span style={{ fontSize: "0.65rem", fontWeight: "600" }}>
                    (20% Advance)
                  </span>
                </button>
              </div>
              <div className="col-6">
                <button
                  className="btn w-100 py-2 rounded-4 d-flex flex-column justify-content-center align-items-center text-white shadow"
                  style={{
                    background: "linear-gradient(135deg, #ff7a00, #ff9500)",
                  }}
                  onClick={() => handleBooking("full")}
                >
                  <div className="fw-bold d-flex align-items-center gap-1">
                    <FaCreditCard /> Pay ₹{totalAmount.toLocaleString()}
                  </div>
                  <span style={{ fontSize: "0.65rem", fontWeight: "600" }}>
                    (Full Online)
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="d-flex flex-column bg-light font-sans min-vh-100 pb-3">
      <div
        className="container-fluid p-0 flex-grow-1"
        style={{ maxWidth: "800px", margin: "0 auto" }}
      >
        {renderSummary()}

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center w-100 mt-5"
            style={{ minHeight: "40vh" }}
          >
            <Loader />
          </div>
        ) : error ? (
          <div className="mx-3 mt-4 alert alert-danger text-center rounded-4 small fw-bold">
            {error}
          </div>
        ) : (
          <div className="px-2 mt-2">
            {cars.length > 0 && (
              <div className="d-flex justify-content-between align-items-center px-2 mb-3 mt-2 animate-fade-in">
                {/* 1. NATIVE STYLE HEADER */}
                <div className="d-flex align-items-baseline">
                  <span
                    className="fw-bolder text-dark"
                    style={{ fontSize: "1.15rem", letterSpacing: "-0.3px" }}
                  >
                    {cars.length}
                  </span>
                  <span
                    className="ms-1 fw-bold"
                    style={{
                      fontSize: "0.8rem",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Vehicles
                  </span>
                </div>

                {/* 2. NATIVE MATERIAL 'CHIP' DROPDOWN */}
                <div className="position-relative d-flex align-items-center">
                  <div
                    className="d-flex align-items-center rounded-pill px-3 py-1 transition-all btn-native-press"
                    style={{
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                    }}
                  >
                    <FaSortAmountDown
                      className="me-2"
                      size={12}
                      style={{ color: "#0ea5e9" }}
                    />

                    <select
                      className="border-0 bg-transparent fw-bold text-dark p-0 m-0 w-auto"
                      style={{
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        outline: "none",
                        boxShadow: "none",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        paddingRight: "2px",
                      }}
                      value={sortType}
                      onChange={(e) => setSortType(e.target.value)}
                    >
                      <option value="price_asc">Price: Low to High</option>
                      <option value="price_desc">Price: High to Low</option>
                      <option value="name_asc">Name: A to Z</option>
                      <option value="name_desc">Name: Z to A</option>
                    </select>

                    {/* Custom Native-looking Chevron */}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ms-1"
                      style={{ pointerEvents: "none" }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {sortedCars.length > 0 ? (
              sortedCars.map((car, index) => {
                const discountedPrice = car.finalPrice;
                const originalPrice =
                  car.crossedOutPrice ||
                  car.finalPrice + (car.discountAmount || 0);

                return (
                  <div
                    className="card border-0 shadow-sm rounded-4 mb-3 mx-1 car-card cursor-pointer overflow-hidden animate-slide-up"
                    key={car.id}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    onClick={() => setSelectedCar(car)}
                  >
                    <div className="card-body p-2 p-sm-3">
                      {/* Reduce gap size on smaller screens */}
                      <div className="d-flex align-items-center gap-2 gap-sm-3">
                        <div
                          className="bg-light rounded-3 d-flex align-items-center justify-content-center border border-light flex-shrink-0"
                          style={{ width: "80px", height: "60px" }}
                        >
                          <img
                            src={car.image}
                            alt={car.name}
                            className="img-fluid"
                            style={{ objectFit: "contain", maxHeight: "100%" }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = CAR_SKELETON;
                            }}
                          />
                        </div>

                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <h6 className="fw-bold mb-0 text-dark text-truncate">
                            {car.name}
                          </h6>
                          <div
                            className="text-muted mb-2 text-truncate"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {car.model}
                          </div>

                          {/* flex-wrap ensures the icons drop to the next line if the screen is too narrow */}
                          <div
                            className="d-flex flex-wrap gap-1 gap-sm-2 text-secondary"
                            style={{ fontSize: "0.65rem" }}
                          >
                            <span className="bg-light px-1 px-sm-2 py-1 rounded border fw-semibold d-flex align-items-center gap-1">
                              <FaUser />
                              {car.seats}
                            </span>
                            <span className="bg-light px-1 px-sm-2 py-1 rounded border fw-semibold d-flex align-items-center gap-1">
                              <FaSuitcase />
                              {car.luggage}
                            </span>
                            <span className="bg-light px-1 px-sm-2 py-1 rounded border fw-semibold d-flex align-items-center gap-1">
                              <FaGasPump />
                              {car.fuel}
                            </span>
                          </div>
                        </div>

                        <div
                          className="text-end d-flex flex-column align-items-end justify-content-center flex-shrink-0"
                          style={{ minWidth: "85px" }}
                        >
                          <small
                            className="text-muted d-block"
                            style={{ fontSize: "0.65rem" }}
                          >
                            Total Fare
                          </small>

                          <div className="d-flex flex-column align-items-end mb-1">
                            <small
                              className="text-muted text-decoration-line-through"
                              style={{ fontSize: "0.7rem" }}
                            >
                              ₹{originalPrice.toLocaleString()}
                            </small>
                            <h5 className="fw-bold m-0 text-success">
                              ₹{discountedPrice.toLocaleString()}
                            </h5>
                          </div>

                          <div
                            className="bg-dark text-white rounded-pill px-2 py-1 fw-bold mt-1 w-100 text-center"
                            style={{ fontSize: "0.65rem" }}
                          >
                            Select
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-5 mt-4">
                <div
                  className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm mb-3"
                  style={{ width: "80px", height: "80px" }}
                >
                  <FaRoute size={30} className="text-muted opacity-50" />
                </div>
                <h6 className="fw-bold text-dark">No rides available</h6>
                <p className="text-muted small">
                  Try modifying your route or date.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedCar && (
        <CarDetailModal
          car={selectedCar}
          onClose={() => setSelectedCar(null)}
        />
      )}

      {/* STYLES */}
      <style>{`
                .cursor-pointer { cursor: pointer; }
                .z-index-1000 { z-index: 1000; }
                .z-index-1 { z-index: 1; }
                
                .car-card { transition: transform 0.15s ease, box-shadow 0.15s ease; position: relative; }
                .car-card:active { transform: scale(0.98); background-color: #f8f9fa; }
                
                .border-dashed { border-top: 1px dashed #dee2e6 !important; }
                .drop-shadow { filter: drop-shadow(0px 8px 12px rgba(0,0,0,0.1)); }
                
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                
                @keyframes slideUpFade { 
                    from { opacity: 0; transform: translateY(15px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }
                .animate-slide-up { opacity: 0; animation: slideUpFade 0.4s ease-out forwards; }
                
                .modal-backdrop-custom {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    z-index: 1050;
                    display: flex;
                    justify-content: center;
                    align-items: flex-end;
                    animation: fadeIn 0.2s ease-out;
                }

                .modal-content-custom {
                    background: white;
                    width: 100%;
                    max-width: 600px;
                    max-height: 90vh;
                    border-radius: 24px 24px 0 0;
                    animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1);
                }

                @keyframes slideUp { 
                    from { transform: translateY(100%); } 
                    to { transform: translateY(0); } 
                }

                @media (min-width: 601px) {
                    .modal-backdrop-custom { align-items: center; }
                    .modal-content-custom {
                        border-radius: 20px;
                        max-height: 85%;
                    }
                }
            `}</style>
    </div>
  );
}